import { db } from './firebase';
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from 'firebase/firestore';

const COLLECTION = 'userNotifications';
const BATCH_LIMIT = 400;

/**
 * Mark all unread bell notifications for a project task as read when the user views the thread.
 */
export async function markTaskNotificationsAsRead(userId: string, taskId: string): Promise<void> {
  if (!userId || !taskId) return;

  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('taskId', '==', taskId),
    where('read', '==', false),
  );

  const snap = await getDocs(q);
  if (snap.empty) return;

  for (let i = 0; i < snap.docs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + BATCH_LIMIT).forEach(d => {
      batch.update(d.ref, { read: true });
    });
    await batch.commit();
  }
}

/**
 * Mark all unread bell notifications for a consolidated group comment thread as read.
 */
export async function markGroupNotificationsAsRead(userId: string, groupCommentKey: string): Promise<void> {
  if (!userId || !groupCommentKey) return;

  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('groupCommentKey', '==', groupCommentKey),
    where('read', '==', false),
  );

  const snap = await getDocs(q);
  if (snap.empty) return;

  for (let i = 0; i < snap.docs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + BATCH_LIMIT).forEach(d => {
      batch.update(d.ref, { read: true });
    });
    await batch.commit();
  }
}

/**
 * Mark unread bell notifications for a PDF/file comment thread as read.
 * Matches `sourceId` = annotation id (`pdf_comment`, `pdf_external_comment`, file `mention`).
 */
export async function markPdfCommentNotificationsAsRead(
  userId: string,
  annotationId: string,
): Promise<void> {
  if (!userId || !annotationId) return;

  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('sourceId', '==', annotationId),
    where('read', '==', false),
  );

  const snap = await getDocs(q);
  if (snap.empty) return;

  for (let i = 0; i < snap.docs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + BATCH_LIMIT).forEach(d => {
      batch.update(d.ref, { read: true });
    });
    await batch.commit();
  }
}

/**
 * Mark all unread bell notifications for an ops task as read.
 */
export async function markOpsTaskNotificationsAsRead(userId: string, opsTaskId: string): Promise<void> {
  if (!userId || !opsTaskId) return;

  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('opsTaskId', '==', opsTaskId),
    where('read', '==', false),
  );

  const snap = await getDocs(q);
  if (snap.empty) return;

  for (let i = 0; i < snap.docs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + BATCH_LIMIT).forEach(d => {
      batch.update(d.ref, { read: true });
    });
    await batch.commit();
  }
}
